/**
 * Advanced cache management system for performance optimization
 */

import { productionLogger } from './production-logger';

interface CacheAnalytics {
  hits: number;
  misses: number;
  updates: number;
  errors: number;
}

interface CacheManagerOptions {
  enableAnalytics?: boolean;
  logLevel?: 'none' | 'error' | 'warn' | 'info' | 'debug';
}

export class CacheManager {
  private options: CacheManagerOptions;
  private serviceWorker: ServiceWorker | null = null;

  constructor(options: CacheManagerOptions = {}) {
    this.options = {
      enableAnalytics: true,
      logLevel: 'info',
      ...options
    };

    // Initialize service worker connection
    this.initServiceWorker();
  }

  private async initServiceWorker(): Promise<void> {
    // CRITICAL: Check navigator exists before accessing (prevents SSR crash)
    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator && navigator.serviceWorker.controller) {
      this.serviceWorker = navigator.serviceWorker.controller;
      this.log('info', '🔗 Cache manager connected to service worker');
    }
  }

  private log(level: string, message: string, ...args: unknown[]): void {
    if (this.options.logLevel === 'none') return;
    
    const levels = ['error', 'warn', 'info', 'debug'];
    const currentLevel = levels.indexOf(this.options.logLevel || 'info');
    const messageLevel = levels.indexOf(level);
    
    if (messageLevel <= currentLevel) {
      // Use productionLogger methods safely
      switch (level) {
        case 'error':
          productionLogger.error(message, ...args);
          break;
        case 'warn':
          productionLogger.warn(message, ...args);
          break;
        case 'info':
          productionLogger.info(message, ...args);
          break;
        case 'debug':
          productionLogger.debug(message, ...args);
          break;
      }
    }
  }

  /**
   * Get cache analytics from the service worker
   */
  async getCacheAnalytics(): Promise<CacheAnalytics | null> {
    if (!this.serviceWorker) {
      this.log('warn', '⚠️ Service worker not available for analytics');
      return null;
    }

    return new Promise((resolve) => {
      const channel = new MessageChannel();
      
      channel.port1.onmessage = (event) => {
        resolve(event.data);
      };

      this.serviceWorker?.postMessage(
        { type: 'GET_CACHE_ANALYTICS' },
        [channel.port2]
      );

      // Timeout after 5 seconds
      setTimeout(() => resolve(null), 5000);
    });
  }

  /**
   * Clear specific cache type
   */
  async clearCache(cacheType: 'admin' | 'products' | 'static' | 'api'): Promise<void> {
    if (!this.serviceWorker) {
      this.log('warn', '⚠️ Service worker not available for cache clearing');
      return;
    }

    this.serviceWorker.postMessage({
      type: 'CLEAR_CACHE',
      data: { cacheType }
    });

    this.log('info', `🗑️ Cache cleared: ${cacheType}`);
  }

  /**
   * Preload a specific route and its chunks
   */
  async preloadRoute(route: string): Promise<void> {
    if (!this.serviceWorker) {
      this.log('warn', '⚠️ Service worker not available for route preloading');
      return;
    }

    this.serviceWorker.postMessage({
      type: 'PRELOAD_ROUTE',
      data: { route }
    });

    this.log('info', `⚡ Route preload requested: ${route}`);
  }

  /**
   * Get cache hit rate for performance monitoring
   */
  async getCacheHitRate(): Promise<number> {
    const analytics = await this.getCacheAnalytics();
    if (!analytics) return 0;

    const total = analytics.hits + analytics.misses;
    return total > 0 ? (analytics.hits / total) * 100 : 0;
  }

  /**
   * Get cache performance summary
   */
  async getCachePerformanceSummary(): Promise<{
    hitRate: number;
    totalRequests: number;
    cacheUpdates: number;
    errors: number;
  } | null> {
    const analytics = await this.getCacheAnalytics();
    if (!analytics) return null;

    const totalRequests = analytics.hits + analytics.misses;
    const hitRate = totalRequests > 0 ? (analytics.hits / totalRequests) * 100 : 0;

    return {
      hitRate: Math.round(hitRate * 100) / 100,
      totalRequests,
      cacheUpdates: analytics.updates,
      errors: analytics.errors
    };
  }

  /**
   * Warm up critical routes for better performance
   */
  async warmupCriticalRoutes(): Promise<void> {
    const criticalRoutes = [
      '/',
      '/products',
      '/admin',
      '/collections',
      '/about'
    ];

    this.log('info', '🔥 Warming up critical routes');

    for (const route of criticalRoutes) {
      await this.preloadRoute(route);
      // Small delay between preloads to avoid overwhelming the browser
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }

  /**
   * Monitor cache performance and log insights
   */
  async monitorCachePerformance(): Promise<void> {
    const summary = await this.getCachePerformanceSummary();
    if (!summary) return;

    this.log('info', '📊 Cache Performance Summary:', {
      hitRate: `${summary.hitRate}%`,
      totalRequests: summary.totalRequests,
      cacheUpdates: summary.cacheUpdates,
      errors: summary.errors
    });

    // Provide performance insights
    if (summary.hitRate < 70) {
      this.log('warn', '⚠️ Cache hit rate is below 70%. Consider reviewing cache strategies.');
    } else if (summary.hitRate > 90) {
      this.log('info', '🎉 Excellent cache performance! Hit rate above 90%.');
    }

    if (summary.errors > 0) {
      this.log('warn', `⚠️ ${summary.errors} cache errors detected. Check network connectivity.`);
    }
  }

  /**
   * Clear all caches (use with caution)
   */
  async clearAllCaches(): Promise<void> {
    const cacheTypes: Array<'admin' | 'products' | 'static' | 'api'> = 
      ['admin', 'products', 'static', 'api'];

    this.log('warn', '🗑️ Clearing all caches');

    for (const cacheType of cacheTypes) {
      await this.clearCache(cacheType);
    }
  }

  /**
   * Force service worker update
   */
  async updateServiceWorker(): Promise<void> {
    if (!this.serviceWorker) {
      this.log('warn', '⚠️ Service worker not available for update');
      return;
    }

    this.serviceWorker.postMessage({ type: 'SKIP_WAITING' });
    this.log('info', '🔄 Service worker update requested');
  }
}

// Export singleton instance
export const cacheManager = new CacheManager();